import type {
  Achievement,
  AppData,
  Category,
  Profile,
} from './types';
import { DATA_VERSION } from './types';
import { newId } from '../lib/id';
import { DEFAULT_THEME } from '../lib/theme';

/**
 * Default expense categories on a fresh install. Order matters — it's the
 * order they show up in pickers and lists.
 */
export const DEFAULT_EXPENSE_CATEGORIES: Omit<Category, 'id' | 'order'>[] = [
  {
    name: 'Food & Dining',
    icon: 'UtensilsCrossed',
    type: 'expense',
    color: '#FF9500',
    bucket503020: 'needs',
  },
  {
    name: 'Gas & Transport',
    icon: 'Car',
    type: 'expense',
    color: '#5856D6',
    bucket503020: 'needs',
  },
  {
    name: 'Housing & Rent',
    icon: 'Home',
    type: 'expense',
    color: '#007AFF',
    bucket503020: 'needs',
  },
  {
    name: 'Utilities',
    icon: 'Zap',
    type: 'expense',
    color: '#FFCC00',
    bucket503020: 'needs',
  },
  {
    name: 'Entertainment',
    icon: 'Film',
    type: 'expense',
    color: '#FF2D55',
    bucket503020: 'wants',
  },
  {
    name: 'Shopping',
    icon: 'ShoppingBag',
    type: 'expense',
    color: '#AF52DE',
    bucket503020: 'wants',
  },
  {
    name: 'Health & Medical',
    icon: 'Heart',
    type: 'expense',
    color: '#FF3B30',
    bucket503020: 'needs',
  },
  {
    name: 'Insurance',
    icon: 'Shield',
    type: 'expense',
    color: '#5AC8FA',
    bucket503020: 'needs',
  },
  {
    name: 'Subscriptions',
    icon: 'CreditCard',
    type: 'expense',
    color: '#FF6482',
    bucket503020: 'wants',
  },
  {
    name: 'Personal Care',
    icon: 'Sparkles',
    type: 'expense',
    color: '#FFD60A',
    bucket503020: 'wants',
  },
  {
    name: 'Education',
    icon: 'GraduationCap',
    type: 'expense',
    color: '#34C759',
    bucket503020: 'needs',
  },
  {
    name: 'Savings Transfer',
    icon: 'PiggyBank',
    type: 'expense',
    color: '#30D158',
    bucket503020: 'savings',
  },
  {
    name: 'Other Expense',
    icon: 'MoreHorizontal',
    type: 'expense',
    color: '#8E8E93',
    bucket503020: 'wants',
  },
];

export const DEFAULT_INCOME_CATEGORIES: Omit<Category, 'id' | 'order'>[] = [
  { name: 'Salary', icon: 'Briefcase', type: 'income', color: '#34C759' },
  { name: 'Freelance', icon: 'Laptop', type: 'income', color: '#007AFF' },
  { name: 'Side Hustle', icon: 'Rocket', type: 'income', color: '#FF9500' },
  { name: 'Investments', icon: 'TrendingUp', type: 'income', color: '#5856D6' },
  { name: 'Gifts Received', icon: 'Gift', type: 'income', color: '#FF2D55' },
  {
    name: 'Other Income',
    icon: 'DollarSign',
    type: 'income',
    color: '#8E8E93',
  },
];

/** Build the fresh category list with stable ids and incremental order. */
export function buildDefaultCategories(): Category[] {
  const all = [...DEFAULT_EXPENSE_CATEGORIES, ...DEFAULT_INCOME_CATEGORIES];
  return all.map((c, i) => ({ ...c, id: newId(), order: i }));
}

/**
 * Achievement catalog. Rule evaluation lives in Step 15 — this defines
 * the surface so the store and UI can iterate over it from day one.
 */
export const ACHIEVEMENT_CATALOG: Omit<Achievement, 'unlockedAt'>[] = [
  {
    id: 'first-step',
    title: 'First Step',
    description: 'Log your first transaction',
    icon: 'Footprints',
  },
  {
    id: 'week-warrior',
    title: 'Week Warrior',
    description: 'Log transactions for 7 days straight',
    icon: 'CalendarCheck',
  },
  {
    id: 'budget-boss',
    title: 'Budget Boss',
    description: 'Stay under budget for 7 consecutive days',
    icon: 'ShieldCheck',
  },
  {
    id: 'self-control',
    title: 'Self Control',
    description: 'Stay under budget for 14 consecutive days',
    icon: 'Timer',
  },
  {
    id: 'month-master',
    title: 'Month Master',
    description: 'Complete a full budget period under budget',
    icon: 'Trophy',
  },
  {
    id: 'savers-start',
    title: "Saver's Start",
    description: 'Set your first savings goal',
    icon: 'PiggyBank',
  },
  {
    id: 'goal-getter',
    title: 'Goal Getter',
    description: 'Reach a savings goal',
    icon: 'Target',
  },
  {
    id: 'tool-time',
    title: 'Tool Time',
    description: 'Use every built-in tool at least once',
    icon: 'Wrench',
  },
  {
    id: 'impulse-resist',
    title: 'Impulse Resist',
    description: 'Wait out an impulse timer and decide not to buy',
    icon: 'Hand',
  },
  {
    id: 'centurion',
    title: 'Centurion',
    description: 'Log 100 transactions',
    icon: 'Award',
  },
  {
    id: 'consistent',
    title: 'Consistent',
    description: 'Use the app for 30 days',
    icon: 'Flame',
  },
  {
    id: 'stellar',
    title: 'Stellar',
    description: 'Rate the app and unlock the Stellar theme',
    icon: 'Sparkles',
  },
];

export function buildDefaultAchievements(): Achievement[] {
  return ACHIEVEMENT_CATALOG.map((a) => ({ ...a }));
}

/** Default profile for a brand-new install (pre-onboarding). */
export const DEFAULT_PROFILE: Profile = {
  name: '',
  budgetModel: 'daily',
  payCycle: 'biweekly',
  payDay: 5, // Friday
  currency: 'USD',
  // heroView intentionally undefined — TodayScreen picks a smart
  // default based on the user's income shape until they toggle.
  rollover: 'continuous',
  workDaysPerWeek: 5,
  onboardingComplete: false,
  walkthroughComplete: false,
  quickPresets: [],
  netWorthHistory: [],
  achievements: emptyAchievementsState(),
};

/**
 * Empty AchievementsState. installedAt gets overwritten by
 * `buildDefaultAppData` so first-launch users carry the actual install
 * timestamp; the placeholder here is just to keep the type shape valid.
 */
export function emptyAchievementsState() {
  return {
    unlocks: [],
    progress: [],
    streaks: {
      daysLogged: { current: 0, best: 0, lastDate: null },
      daysUnderPace: { current: 0, best: 0, lastDate: null },
      noSpendDays: { current: 0, best: 0, lastDate: null },
      pyfStreak: { current: 0, best: 0, lastDate: null },
    },
    installedAt: new Date(0).toISOString(),
    achievementsEnabled: true,
  };
}

/** Build a fully populated AppData for a fresh install. */
export function buildDefaultAppData(): AppData {
  const installedAt = new Date().toISOString();
  return {
    version: DATA_VERSION,
    profile: {
      ...DEFAULT_PROFILE,
      achievements: { ...emptyAchievementsState(), installedAt },
    },
    categories: buildDefaultCategories(),
    transactions: [],
    scheduledItems: [],
    goals: [],
    achievements: buildDefaultAchievements(),
    installedAt: new Date().toISOString(),
    toolsUsed: [],
    theme: DEFAULT_THEME,
    accentColor: 'blue',
    rated: false,
    accounts: [],
  };
}
